import React, { useContext, useState } from 'react';
import {
  AvBreadcrumbs,
  AvTooltip,
  dateFormat,
  EVariant,
  FeatureFlags,
  formatDate,
  getDateFromFilter,
  getDatesFromDefinition,
  getInputValue,
  isDateRangeValue,
  NewRelativeDate,
  RelativeDatePeriod,
  RelativeDateType,
  validateValue,
} from '@fabric-ui/components';
import { generateInnerDefaultFormatter } from '@fabric-ui/components/Table';
import { Edit, MaximizeFullScreen as Maximize, MinimizeFullScreen as Minimize, More, Table } from '@fabric-ui/icons';
import { FilterActive } from '@fabric-ui/icons/colorful';
import { cellContentStyle, flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, capitalize, Divider, IconButton, useTheme } from '@mui/material';
import { HistoricDataIndicator } from '../../../components/Widgets/layout.components';
import { useAvContext } from '../../../context/AvContextProvider';
import { defaultDrillDownHierarchy } from '../constants';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { activeIconStyle, shouldShowDrillDown } from '../CustomDashboardsUtils';
import { useGetDisplayName } from '../hooks/useGetDisplayName';
import { useGetFieldType } from '../hooks/useGetFieldType';
import { AllTypeWidgets, HistoricalFilterMode, TableSubType, TableTypeWidget, TextSubType, TileSubType, WidgetCategory } from '../types';
import { TabsType } from '../types/types';
import { getParamFromURL } from './Clickthrough/utils';
import DrillUpButton from './DrillUpButton';
import { FilterToText } from './FiltersDescription';
import WidgetToolbarActions from './WidgetToolbarActions';

const editIcon = <Edit style={iconSize(18)} />;
const maximizeIcon = <Maximize style={iconSize(18)} />;
const minimizeIcon = <Minimize style={iconSize(18)} />;
const moreIcon = <More style={iconSize(18)} />;
const filterActiveIcon = <FilterActive style={iconSize(18)} />;
const tableIcon = <Table style={{ ...iconSize(18) }} />;

export interface WidgetToolbarProps {
  widget: AllTypeWidgets;
  setWidget?: (newWidget: any) => void;
  isTable?: boolean;
  setIsTable?: any;
}

const WidgetToolbar: React.FC<WidgetToolbarProps> = ({ widget, setWidget, isTable = false, setIsTable }) => {
  const { featureFlags } = useAvContext();
  const theme = useTheme();
  const isHistoricalAnalytics = featureFlags[FeatureFlags.HistoricalAnalytics];
  const { setNewWidget, globalFilters, filters, setFilters, expandedWidgetId, setExpandedWidgetId } = useContext(CustomDashboardContext);
  const isEditMode = getParamFromURL<boolean>(filters.isEditMode);
  const isMaximized = expandedWidgetId === widget.id;

  const [anchorEl, setAnchorEl] = useState<Element>();
  const {
    drillDownHierarchy = defaultDrillDownHierarchy,
    requests: [
      {
        select: {
          dims: [dim],
        },
        filter,
        top,
        sorting,
        projectionId,
        timeRange,
      },
    ],
  } = widget;

  const getDisplayName = useGetDisplayName(projectionId.name);
  const getFieldType = useGetFieldType(projectionId.name);

  const onChangeFilter = (newValues: Record<string, any>) =>
    setFilters({
      ...filters,
      ...Object.fromEntries(Object.entries(newValues).map(([key, value]) => [key, Array.isArray(value) ? value : [value]])),
    });

  const handleFilter = () => {
    if (isEditMode) {
      setNewWidget(widget);
      onChangeFilter({ tab: TabsType.Filters, isEditWidgetOpen: true });
    }
  };
  const handleEdit = () => {
    const newWidget: AllTypeWidgets = {
      ...widget,
      ...(widget.drillDownHierarchy ? { drillDownHierarchy: { ...widget.drillDownHierarchy, activeIndex: 0 } } : {}),
      requests: widget.requests.map(r => ({ ...r, ...(r.top ? { top: { ...r.top, offset: 0 } } : r.top) })),
    };
    setNewWidget(newWidget);
    if (setWidget) {
      setWidget(newWidget);
    }
    onChangeFilter({ tab: TabsType.Data, isEditWidgetOpen: true });
  };

  const handleExpand = () => setExpandedWidgetId(prev => (prev ? undefined : widget.id));
  const toggleShowAsTable = () => setIsTable?.(!isTable);

  const toggleUserMenu = ({ currentTarget }) => setAnchorEl(anchorEl ? undefined : currentTarget);
  const [closeTimeout, setCloseTimeout] = useState<any>();

  const clearCloseTimeout = () => clearTimeout(closeTimeout);

  const closeMenu = () => {
    clearCloseTimeout();
    setCloseTimeout(setTimeout(() => setAnchorEl(undefined), 350));
  };

  const drillUpLevelByIndex = index => {
    if (setWidget) {
      setWidget({
        ...widget,
        requests: [{ ...widget.requests[0], top: { ...widget.requests[0].top, offset: 0 } }],
        drillDownHierarchy: {
          ...drillDownHierarchy,
          activeIndex: index,
          drillDownValues: drillDownHierarchy.drillDownValues.slice(0, index),
        },
      });
    }
  };
  const showBreadcrumbs = !!drillDownHierarchy.activeIndex && !(widget as TableTypeWidget).definition?.custom?.crossTableView;
  const drilldownlist = [{ name: widget.definition.title.title || getDisplayName(dim?.name) }, ...drillDownHierarchy.fields];

  const getFormattedDate = () => {
    if (timeRange?.relative) {
      return capitalize(
        `${RelativeDateType[(timeRange.relative as NewRelativeDate).periodBehaviour.toLowerCase()]} ${timeRange.relative.value} ${
          RelativeDatePeriod[timeRange.relative.unit.toLowerCase()]
        }`
      );
    }

    const dateObject = getDateFromFilter(timeRange ?? {});
    const isRange = Boolean(timeRange?.relative || timeRange?.between);
    const date = timeRange?.relative ? getDatesFromDefinition(dateObject.preset) : validateValue(dateObject.value, isRange);

    if (timeRange?.between) {
      return getInputValue({ date, isRange, preset: dateObject.preset });
    }

    return formatDate(isDateRangeValue(date) ? date?.to : undefined, dateFormat);
  };

  return (
    <Box
      sx={{
        ...flex.justifyBetween,
        pt: isMaximized ? 1.5 : 0,
        pr: isMaximized ? 0.5 : 0,
        pb: 0.25,
      }}>
      <Box sx={{ ...flex.itemsCenter, gap: '10px' }}>
        {shouldShowDrillDown(widget) &&
          (widget.category !== WidgetCategory.Table || !widget.definition.custom.crossTableView) &&
          !!drillDownHierarchy.fields.length && <DrillUpButton widget={widget} />}
        {widget.category === WidgetCategory.Text || showBreadcrumbs ? undefined : (
          <AvTooltip sx={{ fontSize: '18px', fontWeight: 600, ...cellContentStyle, WebkitLineClamp: '2', wordBreak: 'break-word' }}>
            {widget.definition.title.title}
          </AvTooltip>
        )}
        {showBreadcrumbs && (
          <Box className="draggableCancel" sx={{ ...flex.row, height: 'fit-content', flexWrap: 'wrap' }}>
            <AvBreadcrumbs
              breadcrumbs={drilldownlist.slice(0, drillDownHierarchy.activeIndex + 1).map((value, index) => ({
                title: index === 0 ? '' : index === 1 ? getDisplayName(dim.name) : getDisplayName(drilldownlist[index - 1].name),
                value:
                  index === 0
                    ? value.name
                    : generateInnerDefaultFormatter({
                        value: drillDownHierarchy.drillDownValues[index - 1],
                        type: getFieldType({ entityFieldKey: drillDownHierarchy.fields[index - 1].name }),
                        shouldDisplayNull: true,
                      }),
                onClick:
                  index === drilldownlist.length - 1
                    ? undefined
                    : () => {
                        drillUpLevelByIndex(index);
                      },
              }))}
              variant={EVariant.drillDown}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ ...flex.itemsCenter, gap: 1.5 }} className="draggableCancel">
        {isEditMode && (
          <AvTooltip title="Edit Widget">
            <IconButton className="showOnHover" onClick={handleEdit}>
              {editIcon}
            </IconButton>
          </AvTooltip>
        )}
        {![TableSubType.Table, TextSubType.Text, TileSubType.Tile].includes(widget.type as any) && !drillDownHierarchy.activeIndex && (
          <AvTooltip title="Table view">
            <IconButton
              className={!isTable ? 'showOnHover' : undefined}
              sx={isTable ? activeIconStyle(theme) : undefined}
              onClick={toggleShowAsTable}>
              {tableIcon}
            </IconButton>
          </AvTooltip>
        )}
        {isHistoricalAnalytics && timeRange && (
          <HistoricDataIndicator
            hideText
            title={
              <span>
                Historical Data: {getFormattedDate()}
                {featureFlags[FeatureFlags.WidgetHistoricalFilterMode] ? (
                  <>
                    <br />
                    Filter Evaluation Time: {widget.historicalFilterMode === HistoricalFilterMode.HISTORICAL ? 'Historical' : 'Current'}
                  </>
                ) : null}
              </span>
            }
            variant="primary"
          />
        )}
        {filter && (
          <AvTooltip title={<FilterToText filter={filter} top={top} sorting={sorting[0]} projectionId={projectionId.name} />}>
            <IconButton sx={isEditMode ? undefined : { cursor: 'default' }} onClick={handleFilter}>
              {filterActiveIcon}
            </IconButton>
          </AvTooltip>
        )}
        {(isEditMode || (widget.category !== WidgetCategory.Text && !isEditMode)) && (
          <Box onMouseOver={clearCloseTimeout} onMouseLeave={closeMenu}>
            <AvTooltip title="More Options">
              <IconButton onClick={toggleUserMenu} sx={anchorEl ? activeIconStyle(theme) : undefined}>
                {moreIcon}
              </IconButton>
            </AvTooltip>
            {anchorEl && (
              <WidgetToolbarActions
                anchorEl={anchorEl}
                widget={widget}
                isEditMode={isEditMode}
                closeMenu={closeMenu}
                handleExpand={handleExpand}
                globalFilters={globalFilters}
              />
            )}
          </Box>
        )}
        {isMaximized && widget.category !== WidgetCategory.Text && (
          <>
            <Divider orientation="vertical" sx={{ height: 18, alignSelf: 'center' }} />
            <AvTooltip title={isMaximized ? 'Minimize' : 'Full Screen'}>
              <IconButton onClick={handleExpand}>{isMaximized ? minimizeIcon : maximizeIcon}</IconButton>
            </AvTooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default WidgetToolbar;
